import { buildPreviewContenu, getTableElementConfig, isComplexAnswer } from './questionUtils';

/**
 * Adaptateur Question EvalNat → payload "format unifié" (2.4a).
 *
 * Les questions EvalNat stockent leurs données éclatées (type_question +
 * reponses[] + bonne_reponse) ; toute la chaîne moderne (LaunchModal, cartes,
 * pipeline d'impression) consomme le format unifié des BanqueQuestion (un
 * objet `contenu`). buildPreviewContenu fait 90 % de la traduction — cet
 * adaptateur n'ajoute que l'enveloppe :
 *   1. type_question → type ('qcm_multiple' → 'qcm', le multi vit déjà dans
 *      contenu.multi) — le print route sur payload.type ;
 *   2. l'image principale (question.image, champ à part) injectée en élément
 *      additionnel — PrintEnonce ne lit que elements_additionnels ;
 *   3. les tableaux EvalNat (bonne_reponse `[ligne|colonne]`, sans élément
 *      table) reçoivent leur élément table reconstruit — sans lui, le print
 *      les routerait en QCM standard sans choix ;
 *   4. l'identité : _tmpId (clé stable du modal) + _meta.evalNatId (pour
 *      extraire les ids EvalNat dans l'ordre au POST evaluations_nat/) +
 *      _meta.source (badge "EvalNat" au lieu de "TanoEd").
 *
 * ⚠️ Ces payloads sont JETABLES (affichage + print uniquement) : depuis
 * l'abandon du QR code EvalNat, ils ne sont JAMAIS persistés en banque.
 */

const mapType = (typeQuestion) => {
    if (typeQuestion === 'texte') return 'texte';
    if (typeQuestion === 'association') return 'association';
    return 'qcm'; // 'qcm' et 'qcm_multiple' — le multi est porté par contenu.multi
};

// elements_additionnels peut arriver en string JSON selon les serializers.
const parseElements = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'string') {
        try { return JSON.parse(raw); } catch { return null; }
    }
    return raw;
};

export const toPayloadUnifie = (question) => {
    const p = buildPreviewContenu(question);
    if (!p) return null;

    const base = parseElements(p.elements_additionnels);
    const elements = [...(base?.elements || [])];

    // 2. Image principale → élément additionnel (affichée dans l'énoncé print/cartes)
    if (p.image) {
        elements.push({ type: 'image', config: { imageUrl: p.image } });
    }

    // 3. Tableau EvalNat sans élément table → reconstruction depuis bonne_reponse
    //    (même mécanique que Quiz.getTableElementConfig côté élève).
    const contenu = p.contenu || {};
    const aDejaTable = elements.some((e) => e?.type === 'table');
    if (!aDejaTable && (contenu.choix || []).length === 0 && isComplexAnswer(contenu.bonne_reponse || '')) {
        const config = getTableElementConfig({ bonne_reponse: contenu.bonne_reponse });
        if (config) elements.push({ type: 'table', config });
    }

    return {
        type: mapType(p.type_question),
        texte: p.texte,
        contenu,
        elements_additionnels: elements.length > 0 ? { version: 1, elements } : null,
        _tmpId: `evalnat_${question.id}`,
        _meta: {
            evalNatId: question.id,
            source: 'evalnat',
            label: question.nom_label || question.label?.nom_label || question.question_num || '',
            // Question nationale "image-porteuse" : la capture contient l'énoncé
            // COMPLET (consigne + choix inclus). Les renderers print/cartes ne
            // doivent rien afficher d'autre que l'image. `contenu` reste intact
            // (aperçu plein écran et passation en ligne utilisent le structuré).
            ...(p.image ? { imagePorteuse: true } : {}),
        },
    };
};

/** Adapte une liste en filtrant les éventuels nulls (question malformée). */
export const toPayloadsUnifies = (questions) =>
    (questions || []).map(toPayloadUnifie).filter(Boolean);

// ─── Sens inverse (10/10/2026) : une variante GÉNÉRÉE devient une question nationale ───

const LETTRES_VARIANTE = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

// Un choix de QCM rangé comme l'ancienne fenêtre des variantes le rangeait : un texte, une image (son
// adresse), ou un autre élément (son JSON). `valeur` est ce que bonne_reponse vaut pour ce choix.
const versReponseChoix = (choix) => {
    if (typeof choix === 'string') return { reponse: { type: 'texte', contenu: choix }, valeur: choix };
    if (choix?.type === 'image') {
        const image = choix.config?.imageUrl;
        if (!image) throw new Error('Choix en image pas encore enregistré : passer par « Créer une question personnalisée ».');
        return { reponse: { type: 'image', contenu: '', image }, valeur: image };
    }
    const json = JSON.stringify(choix);
    return { reponse: { type: 'element', contenu: json }, valeur: json };
};

/**
 * Le payload d'un générateur d'item national ou de la fenêtre de QCMDashboard (format unifié) → les champs
 * d'une VARIANTE de l'item (Question est_nationale=False, même Label), tels que l'ancienne fenêtre des
 * variantes (AddQuestion, supprimée le 11/10) les envoyait à POST questions/. Relue depuis la base, la
 * variante redonne le même exercice (buildPreviewContenu) : c'est l'aller-retour que vérifient les tests.
 * Formats : QCM (choix en texte, KaTeX compris, en image ou en élément ; une ou plusieurs bonnes réponses),
 * saisie simple, texte à trous, relier, ordonner, classer, tableau à cocher. Tout autre format lève une
 * erreur : une variante ne part jamais à moitié traduite.
 * Le back ne lit que 4 choix pour un QCM (POST questions/, reponses[0] à reponses[3]).
 */
export const versVarianteEvalNat = (payload) => {
    const contenu = payload?.contenu || {};
    const brut = parseElements(payload?.elements_additionnels);
    const elements = brut?.elements || [];
    // Le texte support d'une variante de compréhension voyage avec elle (utils/supportLecture.js)
    const elements_additionnels = {
        version: 1,
        ...(elements.length > 0 ? { elements } : {}),
        ...(brut?.support_lecture ? { support_lecture: brut.support_lecture } : {}),
    };
    const texte = payload?.texte || '';

    const table = elements.find((e) => e?.type === 'table');
    if (payload?.type === 'qcm' && table && (contenu.choix || []).length === 0) {
        // Tableau à cocher : une ligne par affirmation, réponse [ligne|colonne] (makeVraiFaux)
        return {
            texte,
            type_question: 'qcm',
            reponses: (table.config?.lignes || []).map((ligne) => ({ type: 'texte', contenu: ligne })),
            bonne_reponse: contenu.bonne_reponse,
            elements_additionnels,
        };
    }
    if (payload?.type === 'qcm') {
        // Plusieurs bonnes réponses : 'qcm_multiple', bonne_reponse = la liste JSON de leurs valeurs
        const choix = contenu.choix || [];
        if (choix.length > 4) throw new Error(`QCM à ${choix.length} choix : le back n'en lit que 4.`);
        const reponses = choix.map(versReponseChoix);
        const lettres = Array.isArray(contenu.bonne_reponse) ? contenu.bonne_reponse : [contenu.bonne_reponse];
        const indices = [...new Set(lettres.map((l) => LETTRES_VARIANTE.indexOf(l)))].sort((a, b) => a - b);
        if (indices.length === 0 || indices.some((i) => i < 0 || i >= choix.length)) {
            throw new Error('Bonne réponse introuvable parmi les choix.');
        }
        const valeurs = indices.map((i) => reponses[i].valeur);
        return {
            texte,
            type_question: valeurs.length > 1 ? 'qcm_multiple' : 'qcm',
            reponses: reponses.map((r) => r.reponse),
            // La liste triée comme le widget de l'élève la renvoie (QCMMultiEleve) et comme la banque la range
            // (resolveBonneReponse) : rangée dans l'ordre des lettres (ancienne fenêtre), une réponse juste était
            // comptée fausse dès que cet ordre différait de l'ordre alphabétique (11/10/2026)
            bonne_reponse: valeurs.length > 1 ? JSON.stringify([...valeurs].sort()) : valeurs[0],
            elements_additionnels,
        };
    }
    if (payload?.type === 'texte') {
        if (contenu.comparaison || contenu.reponses_acceptees) {
            throw new Error('Saisie à plusieurs écritures non prise en charge pour une variante.');
        }
        return {
            texte,
            type_question: 'texte',
            reponses: [{ type: 'texte', strict: Boolean(contenu.strict), suffixe: contenu.suffixe || '' }],
            bonne_reponse: String(contenu.bonne_reponse ?? ''),
            elements_additionnels,
        };
    }
    if (payload?.type === 'association' && contenu.subType === 'texte_trou') {
        // Texte à trous, sous la forme d'AddQuestion : une ligne par trou, puis la ligne trou_meta, que
        // buildPreviewContenu et le correcteur relisent (mode, strict, trous sans ordre).
        const phrase = contenu.phrase || '';
        if (phrase.includes('[') && phrase.includes('|')) {
            throw new Error('Phrase à trous illisible : « [ » et « | » y feraient lire un tableau.');
        }
        // [^{}] : un trou dans un \dfrac s'écrit {{{3}}}, le mot est 3 (extractTrouWords)
        const mots = [...phrase.matchAll(/\{\{([^{}]+)\}\}/g)].map((m) => m[1]);
        if (mots.length === 0) throw new Error('Phrase à trous sans trou.');
        return {
            texte,
            type_question: 'association',
            reponses: [
                ...mots.map((mot) => ({ type: 'trou', contenu: mot, isAnswer: true })),
                ...(contenu.distracteurs || []).map((mot) => ({ type: 'trou', contenu: mot, isAnswer: false })),
                {
                    type: 'trou_meta',
                    phrase,
                    mode: contenu.mode || 'drag',
                    strict: Boolean(contenu.strict),
                    interchangeables: contenu.trousInterchangeables || [],
                },
            ],
            bonne_reponse: phrase,
            elements_additionnels,
        };
    }
    if (payload?.type === 'association' && contenu.subType === 'relier') {
        // Les paires, puis les cibles leurres ; la réponse : les paires dans leur ordre
        const pairs = contenu.pairs || [];
        return {
            texte,
            type_question: 'association',
            reponses: [
                ...pairs.map((p) => ({ type: 'paire', source: p.source, target: p.target })),
                ...(contenu.cibles_leurres || []).map((l) => ({ type: 'paire_leurre', target: l.target, code: l.code || '' })),
            ],
            bonne_reponse: JSON.stringify(pairs.map((p) => [p.source, p.target])),
            elements_additionnels,
        };
    }
    if (payload?.type === 'association' && contenu.subType === 'ordonner') {
        const items = contenu.items || [];
        return {
            texte,
            type_question: 'association',
            reponses: items.map((item, position) => ({ type: 'ordre', contenu: item, position })),
            bonne_reponse: JSON.stringify(items),
            elements_additionnels,
        };
    }
    if (payload?.type === 'association' && contenu.subType === 'classer') {
        const categories = contenu.categories || [];
        return {
            texte,
            type_question: 'association',
            reponses: categories.map((c) => ({ type: 'categorie', contenu: c.name, items: c.items || [] })),
            bonne_reponse: JSON.stringify(Object.fromEntries(categories.map((c) => [c.name, c.items || []]))),
            elements_additionnels,
        };
    }
    throw new Error(`Format non pris en charge pour une variante d'item national : ${payload?.type}${contenu.subType ? ` (${contenu.subType})` : ''}.`);
};

// Les champs d'une variante dans le corps de la requête, clé par clé comme l'ancienne fenêtre des variantes.
// Saisie et association : reponses en une chaîne JSON, que le back garde telle quelle ; un QCM part choix par
// choix (reponses[i][...]), le back n'en lit que ces clés.
const ajouterChamps = (formData, variante) => {
    formData.append('texte', variante.texte || 'Question');
    formData.append('type_question', variante.type_question);
    formData.append('est_nationale', false);
    formData.append('elements_additionnels', JSON.stringify(variante.elements_additionnels));
    if (variante.type_question === 'texte' || variante.type_question === 'association') {
        formData.append('reponses', JSON.stringify(variante.reponses));
    } else {
        variante.reponses.forEach((r, index) => {
            formData.append(`reponses[${index}][contenu]`, r.contenu);
            formData.append(`reponses[${index}][type]`, r.type);
            if (r.image) formData.append(`reponses[${index}][image]`, r.image);
        });
    }
    formData.append('bonne_reponse', variante.bonne_reponse);
    return formData;
};

/**
 * Le corps de POST questions/ pour une variante : jamais nationale, rattachée à l'item (label_id) et à
 * l'établissement ; generator_key marque la variante générée.
 */
export const formDataVariante = (variante, { labelId, etablissementId, generatorKey }) => {
    const formData = ajouterChamps(new FormData(), variante);
    formData.append('label_id', parseInt(labelId, 10));
    formData.append('etablissement', etablissementId);
    if (generatorKey) formData.append('generator_key', generatorKey);
    return formData;
};

/** Le corps de PATCH questions/<id>/ pour corriger une variante en base : ses champs seulement (même item). */
export const formDataCorrection = (variante) => ajouterChamps(new FormData(), variante);
